import { useEffect, useMemo, useState } from 'react';
import PageHeader from '../../../../shared/components/layout/PageHeader';
import ConfirmDialog from '../../../../shared/components/ui/ConfirmDialog';
import { PROFILE_STORES_SELF, PROFILE_STORES_UPDATE } from '../../../../shared/constants/endpoints';
import './StoreSettings.css';

const SETTINGS_KEY_PREFIX = 'zopeo_store_notification_settings_';

const DEFAULT_PREFERENCES = {
  queueAlerts: {
    sms: true,
    whatsapp: true,
    email: false
  },
  appointmentReminders: {
    sms: true,
    whatsapp: true
  },
  offerNotifications: {
    sms: false,
    whatsapp: false
  }
};

const NOTIFICATION_GROUPS = [
  {
    key: 'queueAlerts',
    title: 'Queue Alerts',
    items: [
      { key: 'sms', label: 'SMS', description: 'Alert customers when they are 3 positions away.' },
      { key: 'whatsapp', label: 'WhatsApp', description: 'Send the same queue alerts on WhatsApp.' },
      { key: 'email', label: 'Email', description: 'Not recommended for time-sensitive queue updates.' }
    ]
  },
  {
    key: 'appointmentReminders',
    title: 'Appointment Reminders',
    items: [
      { key: 'sms', label: 'SMS', description: 'Send reminder one hour before appointment.' },
      { key: 'whatsapp', label: 'WhatsApp', description: 'Send reminder one day before appointment.' }
    ]
  },
  {
    key: 'offerNotifications',
    title: 'Offer Notifications',
    items: [
      { key: 'sms', label: 'SMS', description: 'Notify customers when new offers are available.' },
      { key: 'whatsapp', label: 'WhatsApp', description: 'Send offer updates on WhatsApp.' }
    ]
  }
];

const DEFAULT_HOURS = [
  { day: 'Monday', open: '09:00', close: '21:00', enabled: true },
  { day: 'Tuesday', open: '09:00', close: '21:00', enabled: true },
  { day: 'Wednesday', open: '09:00', close: '21:00', enabled: true },
  { day: 'Thursday', open: '09:00', close: '21:00', enabled: true },
  { day: 'Friday', open: '09:00', close: '21:00', enabled: true },
  { day: 'Saturday', open: '10:00', close: '22:00', enabled: true },
  { day: 'Sunday', open: '10:00', close: '22:00', enabled: true }
];

const INTEGRATIONS = [
  { key: 'whatsappConnected', label: 'WhatsApp Business', description: 'Send automated messages to customers' },
  { key: 'googleCalConnected', label: 'Google Calendar', description: 'Sync appointments with Google Calendar' },
  { key: 'razorpayConnected', label: 'Razorpay', description: 'Accept online payments from customers' },
  { key: 'tallyConnected', label: 'Tally', description: 'Sync invoices and billing data' }
];

const readPreferences = (storeUuid) => {
  if (!storeUuid || typeof window === 'undefined') {
    return DEFAULT_PREFERENCES;
  }
  try {
    const raw = window.localStorage.getItem(`${SETTINGS_KEY_PREFIX}${storeUuid}`);
    if (!raw) {
      return DEFAULT_PREFERENCES;
    }
    const parsed = JSON.parse(raw);
    return {
      ...DEFAULT_PREFERENCES,
      ...parsed,
      queueAlerts: { ...DEFAULT_PREFERENCES.queueAlerts, ...(parsed.queueAlerts || {}) },
      appointmentReminders: { ...DEFAULT_PREFERENCES.appointmentReminders, ...(parsed.appointmentReminders || {}) },
      offerNotifications: { ...DEFAULT_PREFERENCES.offerNotifications, ...(parsed.offerNotifications || {}) }
    };
  } catch (error) {
    return DEFAULT_PREFERENCES;
  }
};

const persistPreferences = (storeUuid, preferences) => {
  if (!storeUuid || typeof window === 'undefined') {
    return;
  }
  window.localStorage.setItem(`${SETTINGS_KEY_PREFIX}${storeUuid}`, JSON.stringify(preferences));
};

const StoreSettings = ({ request, storeToken, storeUuid, onToast, onStoreLogout }) => {
  const [preferences, setPreferences] = useState(() => readPreferences(storeUuid));
  const [pendingAction, setPendingAction] = useState(null);
  const [dangerBusy, setDangerBusy] = useState(false);
  const [chargesTaxSeparately, setChargesTaxSeparately] = useState(false);
  const [taxSaving, setTaxSaving] = useState(false);

  // Store profile state
  const [storeProfile, setStoreProfile] = useState(null);
  const [formValues, setFormValues] = useState({
    storeName: '',
    tagline: '',
    address: '',
    phone: '',
    email: ''
  });
  const [savedValues, setSavedValues] = useState({
    storeName: '',
    tagline: '',
    address: '',
    phone: '',
    email: ''
  });

  const isDirty = useMemo(() => {
    return Object.keys(formValues).some((key) => formValues[key] !== savedValues[key]);
  }, [formValues, savedValues]);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await request(PROFILE_STORES_SELF, {
          method: 'GET',
          requestName: 'store-settings.fetch-profile'
        });
        const data = response?.data || response;
        setStoreProfile(data);
        setChargesTaxSeparately(Boolean(data.chargesTaxSeparately));
        const values = {
          storeName: data.storeName || data.name || '',
          tagline: data.tagline || '',
          address: data.address || '',
          phone: data.phone || data.contactPhone || '',
          email: data.email || data.contactEmail || ''
        };
        setFormValues(values);
        setSavedValues(values);
      } catch (error) {
        // Profile fetch failed — fields stay empty
      }
    };
    if (storeToken) {
      fetchProfile();
    }
  }, [request, storeToken]);

  const handleFieldChange = (field, value) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleDiscard = () => {
    setFormValues({ ...savedValues });
  };

  const handleSave = () => {
    setSavedValues({ ...formValues });
    onToast?.({ message: 'Settings saved successfully.', variant: 'success' });
  };

  const operatingHours = useMemo(() => {
    if (storeProfile?.operatingHours && Array.isArray(storeProfile.operatingHours)) {
      return storeProfile.operatingHours;
    }
    return DEFAULT_HOURS;
  }, [storeProfile]);

  const handleToggleTax = async () => {
    if (taxSaving || !storeUuid) {
      return;
    }
    const next = !chargesTaxSeparately;
    setChargesTaxSeparately(next);
    setTaxSaving(true);
    try {
      await request(PROFILE_STORES_UPDATE(storeUuid), {
        method: 'PUT',
        body: JSON.stringify({ storeUuid, chargesTaxSeparately: next }),
        requestName: 'store-settings.update-tax-flag'
      });
      onToast?.({ message: 'Tax billing preference saved.', variant: 'success' });
    } catch (error) {
      setChargesTaxSeparately(!next);
      onToast?.({ message: 'Failed to update tax preference. Please try again.', variant: 'error' });
    } finally {
      setTaxSaving(false);
    }
  };

  const togglePreference = (groupKey, channelKey) => {
    setPreferences((previous) => {
      const next = {
        ...previous,
        [groupKey]: {
          ...previous[groupKey],
          [channelKey]: !previous[groupKey][channelKey]
        }
      };
      persistPreferences(storeUuid, next);
      return next;
    });
  };

  const dialogConfig = useMemo(() => {
    if (pendingAction === 'delete-queue') {
      return {
        title: 'Delete all queue data?',
        description: (
          <p>
            This removes local queue snapshots and resets queue-related preferences for this store.
            This action cannot be undone.
          </p>
        ),
        confirmLabel: 'Delete Queue Data'
      };
    }
    if (pendingAction === 'delete-account') {
      return {
        title: 'Delete account?',
        description: (
          <p>
            This permanently deletes your store account. You will be logged out immediately and cannot recover this data.
          </p>
        ),
        confirmLabel: 'Delete Account'
      };
    }
    return null;
  }, [pendingAction]);

  const handleDangerConfirm = async () => {
    if (!pendingAction) {
      return;
    }
    if (pendingAction === 'delete-queue') {
      setDangerBusy(true);
      try {
        const response = await request('/v1/profile/stores/queue-data/delete', {
          method: 'POST',
          requestName: 'store-settings.delete-queue-data'
        });
        if (typeof window !== 'undefined' && storeUuid) {
          window.localStorage.removeItem(`${SETTINGS_KEY_PREFIX}${storeUuid}`);
        }
        setPreferences(DEFAULT_PREFERENCES);
        const deletedCount = Number(response?.data?.softDeletedEntries ?? 0);
        onToast?.({
          message: deletedCount > 0
            ? `Queue data deleted successfully (${deletedCount} records).`
            : 'Queue data is already cleared.',
          variant: 'success'
        });
      } catch (error) {
        onToast?.({ message: 'Failed to delete queue data. Please try again.', variant: 'error' });
      } finally {
        setDangerBusy(false);
        setPendingAction(null);
      }
      return;
    }

    setDangerBusy(true);
    try {
      await request(`/v1/profile/stores/delete?storeUuid=${encodeURIComponent(storeUuid)}`, {
        method: 'POST',
        requestName: 'store-settings.delete-account'
      });
      onToast?.({ message: 'Account deleted successfully', variant: 'success' });
      onStoreLogout?.();
    } catch (error) {
      onToast?.({ message: 'Failed to delete account. Please contact support.', variant: 'error' });
    } finally {
      setDangerBusy(false);
      setPendingAction(null);
    }
  };

  return (
    <section className="section store-settings-shell">
      <PageHeader
        title="Settings"
        description="Manage your store profile, hours, notifications, and integrations."
        actions={
          <div className="store-settings-header-actions">
            <button
              type="button"
              className="btn secondary small"
              disabled={!isDirty}
              onClick={handleDiscard}
            >
              Discard
            </button>
            <button
              type="button"
              className="btn primary small"
              disabled={!isDirty}
              onClick={handleSave}
            >
              Save changes
            </button>
          </div>
        }
      />

      {/* Store Information */}
      <div className="settings-card">
        <div className="settings-card-header">
          <h3>Store Information</h3>
          <p>Basic details about your store visible to customers.</p>
        </div>
        <div className="settings-form-grid">
          <div className="settings-field">
            <div className="settings-field-label">
              <h4>Store Name</h4>
              <p>Your store's display name</p>
            </div>
            <input
              type="text"
              className="settings-input"
              value={formValues.storeName}
              onChange={(e) => handleFieldChange('storeName', e.target.value)}
              placeholder="Enter store name"
            />
          </div>
          <div className="settings-field">
            <div className="settings-field-label">
              <h4>Tagline</h4>
              <p>A short description shown below name</p>
            </div>
            <input
              type="text"
              className="settings-input"
              value={formValues.tagline}
              onChange={(e) => handleFieldChange('tagline', e.target.value)}
              placeholder="Enter tagline"
            />
          </div>
          <div className="settings-field">
            <div className="settings-field-label">
              <h4>Address</h4>
              <p>Physical store location</p>
            </div>
            <input
              type="text"
              className="settings-input"
              value={formValues.address}
              onChange={(e) => handleFieldChange('address', e.target.value)}
              placeholder="Enter address"
            />
          </div>
          <div className="settings-field">
            <div className="settings-field-label">
              <h4>Phone</h4>
              <p>Customer-facing contact number</p>
            </div>
            <input
              type="tel"
              className="settings-input"
              value={formValues.phone}
              onChange={(e) => handleFieldChange('phone', e.target.value)}
              placeholder="Enter phone number"
            />
          </div>
          <div className="settings-field">
            <div className="settings-field-label">
              <h4>Email</h4>
              <p>Receive reports and alerts</p>
            </div>
            <input
              type="email"
              className="settings-input"
              value={formValues.email}
              onChange={(e) => handleFieldChange('email', e.target.value)}
              placeholder="Enter email address"
            />
          </div>
        </div>
      </div>

      {/* Business Hours */}
      <div className="settings-card">
        <div className="settings-card-header">
          <h3>Business Hours</h3>
          <p>Operating hours displayed to customers.</p>
        </div>
        <div className="settings-hours-grid">
          {operatingHours.map((slot) => (
            <div key={slot.day} className="settings-hours-row">
              <span className="settings-hours-day">{slot.day}</span>
              <span className="settings-hours-time">
                {slot.enabled ? `${slot.open} – ${slot.close}` : 'Closed'}
              </span>
              <span className={`settings-hours-status${slot.enabled ? ' open' : ' closed'}`}>
                {slot.enabled ? 'Open' : 'Closed'}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Notifications */}
      <div className="settings-card">
        <div className="settings-card-header">
          <h3>Notifications</h3>
          <span className="store-settings-caption">Changes save automatically</span>
        </div>
        {NOTIFICATION_GROUPS.map((group) => (
          <div key={group.key} className="store-settings-group">
            <h4>{group.title}</h4>
            <div className="store-settings-group-list">
              {group.items.map((item) => {
                const isEnabled = Boolean(preferences[group.key]?.[item.key]);
                return (
                  <div key={`${group.key}-${item.key}`} className="store-settings-toggle-row">
                    <div className="store-settings-toggle-copy">
                      <span className="store-settings-toggle-label">{item.label}</span>
                      <p>{item.description}</p>
                    </div>
                    <button
                      type="button"
                      className={`store-settings-switch${isEnabled ? ' enabled' : ''}`}
                      role="switch"
                      aria-checked={isEnabled}
                      aria-label={`${group.title} ${item.label}`}
                      onClick={() => togglePreference(group.key, item.key)}
                    >
                      <span className="store-settings-switch-thumb" aria-hidden="true" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Billing & Taxes */}
      <div className="settings-card">
        <div className="settings-card-header">
          <h3>Billing &amp; Taxes</h3>
          <span className="store-settings-caption">Changes save automatically</span>
        </div>
        <div className="store-settings-group">
          <div className="store-settings-group-list">
            <div className="store-settings-toggle-row">
              <div className="store-settings-toggle-copy">
                <span className="store-settings-toggle-label">Charge taxes separately at billing</span>
                <p>
                  Turn on if your listed prices are before tax and you add GST at in-store billing.
                  Customers then see a &ldquo;taxes charged at store&rdquo; note on their appointment bill.
                  Leave off if your prices already include all taxes.
                </p>
              </div>
              <button
                type="button"
                className={`store-settings-switch${chargesTaxSeparately ? ' enabled' : ''}`}
                role="switch"
                aria-checked={chargesTaxSeparately}
                aria-label="Charge taxes separately at billing"
                disabled={taxSaving}
                onClick={handleToggleTax}
              >
                <span className="store-settings-switch-thumb" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Integrations */}
      <div className="settings-card">
        <div className="settings-card-header">
          <h3>Integrations</h3>
          <p>Third-party services connected to your store.</p>
        </div>
        <div className="settings-integrations-list">
          {INTEGRATIONS.map((integration) => {
            const isConnected = Boolean(storeProfile?.[integration.key]);
            return (
              <div key={integration.key} className="settings-integration-row">
                <div className="settings-integration-info">
                  <span className="settings-integration-label">{integration.label}</span>
                  <p>{integration.description}</p>
                </div>
                <span className={`settings-badge${isConnected ? ' connected' : ''}`}>
                  {isConnected ? 'Connected' : 'Not connected'}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Danger Zone */}
      <div className="card store-settings-danger-zone">
        <h3>Danger Zone</h3>
        <div className="store-settings-danger-item">
          <div>
            <h4>Delete all queue data</h4>
            <p>Permanently removes all locally stored queue history snapshots.</p>
          </div>
          <button type="button" className="btn danger small" onClick={() => setPendingAction('delete-queue')}>
            Delete Queue Data
          </button>
        </div>
        <div className="store-settings-danger-item">
          <div>
            <h4>Delete account</h4>
            <p>This cannot be undone.</p>
          </div>
          <button type="button" className="btn danger small" onClick={() => setPendingAction('delete-account')}>
            Delete Account
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={Boolean(dialogConfig)}
        title={dialogConfig?.title || 'Confirm action'}
        description={dialogConfig?.description || null}
        confirmLabel={dialogConfig?.confirmLabel || 'Confirm'}
        confirmVariant="danger"
        busy={dangerBusy}
        onCancel={() => setPendingAction(null)}
        onConfirm={handleDangerConfirm}
      />
    </section>
  );
};

export default StoreSettings;
